/**
 * Responsibility: describe the river valley biome — low country with water winding through the bottom of it.
 *
 * ## Its own biome, by the same test as badlands
 *
 * Does the feature need its own *base* elevation, or only particular ground? This needs a **gentle base with a channel
 * cut through it**, which no existing biome provides: forest has its own shape and rolling hills has four times the
 * relief this wants. So it is a biome. The channel itself lives in `world/riverChannel.js` rather than here, because a
 * river is not a kind of land — it is something that crosses land — and a wooded valley or a city on a river should be
 * able to adopt the same water without a copy of the arithmetic.
 *
 * ## Three fields, each with one job
 *
 * - a very long-wavelength **base**, which is the regional valley floor and the level the water follows
 * - a shorter **hill** field on top, so the land has shape without becoming rolling hills
 * - the **channel** field, which cuts down to a level set by the base *alone*
 *
 * That last point is the one that matters. A real river's grade does not care how tall the hills it cuts through are,
 * so the floor ignores the hill field — and the visible consequence is that the water comes out wider where the land is
 * low and narrower where a hill crowds it, with nothing in the code saying so.
 *
 * ## One terrain function, read by both the ground and the water
 *
 * `createTerrain` is the single authoritative source for the land height and the water level at a coordinate. Both
 * `createElevation` and `createStructures` go through it, which is what stops the water from ever disagreeing with the
 * ground it is supposed to be lying in — the same reason the city derives its footprints once.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createFbm, toUnitRange } from '../noise.js';
import { createRiverChannel } from '../riverChannel.js';
import { createWaterSurface, createWaterClearance } from '../waterSurface.js';
import { createBridges, createBridgeClearance } from '../bridges.js';
import { RIVER, WATER, PALETTE, WATER_PALETTE } from '../../config.js';

/**
 * The land height and water level at any coordinate, for one seed.
 *
 * @param {number} seed - Base world seed.
 * @returns {{elevation: Function, waterLevel: Function, insideChannel: Function}}
 */
function createTerrain(seed) {
  /*
    Very long wavelength. This is the regional valley floor, and the water level follows it — so the slower it drifts,
    the less a river appears to climb when you follow it a long way.
  */
  const baseField = createFbm(seed, 'base:river', {
    octaves: 2,
    baseFrequency: 1 / RIVER.BASE_WAVELENGTH,
    persistence: 0.45,
    lacunarity: RIVER.LACUNARITY,
  });

  const hillField = createFbm(seed, 'hills:river', {
    octaves: RIVER.HILL_OCTAVES,
    baseFrequency: 1 / RIVER.HILL_WAVELENGTH,
    persistence: RIVER.HILL_PERSISTENCE,
    lacunarity: RIVER.LACUNARITY,
  });

  const channel = createRiverChannel(seed, 'river');

  /** Regional floor at a coordinate, without hills and without the channel. */
  const baseAt = (x, z) => RIVER.BASE_HEIGHT + toUnitRange(baseField(x, z)) * RIVER.BASE_RELIEF;

  return {
    /**
     * Ground height, in world units and never negative.
     *
     * @param {number} x
     * @param {number} z
     * @returns {number}
     */
    elevation(x, z) {
      const base = baseAt(x, z);

      /*
        Remapped to 0..1 so the hills only ever *add*. A negative contribution would let land fall below the base
        outside the channel, which would pool water in hollows — scattered ponds instead of a river, which is the whole
        thing this biome is built to avoid.
      */
      const land = base + toUnitRange(hillField(x, z)) * RIVER.HILL_RELIEF;

      return Math.max(0, channel.carve(land, base, channel.fromCentre(x, z)));
    },

    /**
     * Height of the water surface at a coordinate, whether or not there is water there.
     *
     * @param {number} x
     * @param {number} z
     * @returns {number}
     */
    waterLevel(x, z) {
      return channel.waterLevel(baseAt(x, z));
    },

    /**
     * How deep this biome's water is at a coordinate, before biome weight is applied.
     *
     * **Measured against this biome's own ground, never against the blended ground.** That distinction is the whole
     * bug that shipped first: the channel field is a global function of position, so asking whether the *blended*
     * ground was below the river's water level drew the river network across every neighbour whose land happened to be
     * low — water stood on 30 of 41 city tiles and 24 of 40 farmland tiles at a river weight of zero.
     *
     * Owning the depth here also fixes the width. A river's breadth now comes from its own channel geometry rather
     * than from a comparison against whatever land it is fading into, so it tapers to nothing at a border instead of
     * spreading out into a sheet.
     *
     * **Signed, and deliberately not clamped at zero.** The waterline is found by interpolating this along a grid cell
     * edge, so a dry point has to report how far *above* the water it is. Clamping it to zero puts the crossing at the
     * dry end and nothing gets clipped — the bank comes out as a staircase of whole cells. That mistake has now been
     * made twice in this file's history, once in each direction.
     *
     * @param {number} x
     * @param {number} z
     * @returns {number} Positive under water, negative on dry land. Continuous everywhere.
     */
    ownDepth(x, z) {
      const base = baseAt(x, z);
      const land = base + toUnitRange(hillField(x, z)) * RIVER.HILL_RELIEF;

      return channel.waterLevel(base) - channel.carve(land, base, channel.fromCentre(x, z));
    },

    /**
     * Whether a coordinate is inside the valley at all.
     *
     * The cheap rejection the water pass leans on: one unblended noise sample against the biome weight and blended
     * terrain samples it saves, and most of the biome is nowhere near a channel.
     *
     * @param {number} x
     * @param {number} z
     * @returns {boolean}
     */
    insideChannel(x, z) {
      return channel.fromCentre(x, z) < 1;
    },

    /**
     * Whether a coordinate is close enough to a valley that something standing there might reach the water.
     *
     * Looser than `insideChannel` on purpose — see `WATER.CLEARANCE_GATE`. A wide object centred outside the valley can
     * still overhang the river, and those are precisely the cases the shore test exists for.
     *
     * @param {number} x
     * @param {number} z
     * @returns {boolean}
     */
    nearChannel(x, z) {
      return channel.fromCentre(x, z) < WATER.CLEARANCE_GATE;
    },

    /**
     * Distance from the channel's centre line as a fraction: 0 on the line, 1 at the valley edge, above 1 outside.
     *
     * Exposed for the bridge search, which walks *down* this to find the water. It cannot use the depth for that: outside
     * the valley the depth is just the water level minus the hills, so its gradient follows the terrain instead of
     * pointing at the channel — measured, a search on depth alone found a crossing for only 3% of candidates.
     *
     * @param {number} x
     * @param {number} z
     * @returns {number}
     */
    fromChannelCentre(x, z) {
      return channel.fromCentre(x, z);
    },
  };
}

export const river = {
  id: 'river',
  label: 'River valley',

  palette: PALETTE.river,

  /**
   * The water gets its own gradient, interpolated by depth rather than by height.
   *
   * Drawn in the ground's colours a river is invisible; drawn in one flat colour it is the uniform expanse the steering
   * file forbids. A depth ramp gives it a bright core that fades out at the bank, which is what makes it read as water.
   */
  structurePalette: WATER_PALETTE,

  suitabilityBias: RIVER.SUITABILITY_BIAS,

  /**
   * Build the elevation function for this biome.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number) => number} Elevation in world units, always >= 0.
   */
  createElevation(seed) {
    return createTerrain(seed).elevation;
  },

  /**
   * Highest elevation this biome can produce, in world units: the base at its highest, plus the tallest hill.
   *
   * The channel only ever cuts down, so it cannot raise this.
   */
  maxElevation: RIVER.BASE_HEIGHT + RIVER.BASE_RELIEF + RIVER.HILL_RELIEF,

  /**
   * Build the structure placement function: the water surface itself.
   *
   * **Water is a structure here, and that decision is what made this affordable.** The alternative was to clamp the
   * drawn terrain to the water level and colour it differently, which means teaching the heightfield what water is on
   * the hottest path in the project and teaching the palette to colour something other than elevation. Going through
   * the structure pipeline instead needed no change to the heightfield, the palette, `tile.js` or the renderer.
   *
   * **The depth belongs to this biome and the ground only decides what height the surface sits at.** Getting that
   * direction backwards is what shipped first, and it flooded a third of the world: asking whether the blended ground
   * was below the river's water level drew the channel network across every neighbour with low land. Scaling this
   * biome's own depth by its own weight confines the water to it, and makes the river taper out at a border rather
   * than spread.
   *
   * @param {number} seed - Base world seed.
   * @returns {(region: object, context: object) => Array<object>}
   */
  createStructures(seed) {
    const terrain = createTerrain(seed);

    const surface = createWaterSurface({
      ownDepthAt: terrain.ownDepth,

      /*
        Flat by construction: `riverChannel.waterLevel` is a function of the regional base only and explicitly ignores the
        local land, which is the property the surface now depends on. The bridges already used this same level, so a deck
        and the water under it cannot disagree about where the surface is.
      */
      waterLevelAt: terrain.waterLevel,
      couldHoldWater: terrain.insideChannel,
      grid: RIVER.WATER_GRID,
      maxDepth: RIVER.INCISION - RIVER.WATER_DROP,
    });

    const bridges = createBridges(seed, 'river', {
      ownDepthAt: terrain.ownDepth,
      waterLevelAt: terrain.waterLevel,
      guideAt: terrain.fromChannelCentre,
    });

    /*
      Bridges first, so a crossing reads as the thing the water runs under rather than something laid on top of it. Order
      within a tile's structure list has no rendering consequence — they are all one buffer — but it keeps the intent
      readable.
    */
    return function collectStructures(region, context) {
      return [...bridges(region, context), ...surface(region, context)];
    };
  },

  /**
   * Publish the water so nothing else stands in it.
   *
   * **One line here keeps trees, towers, rock columns, farm buildings and dishes all out of the river**, because every
   * one of them already consults the cross-biome reservation. Nothing in any other biome had to change — which is the
   * payoff for water being a structure with a reservation rather than a special case in the terrain.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number, radius: number, context: object) => boolean}
   */
  createReservation(seed) {
    const terrain = createTerrain(seed);

    const water = createWaterClearance({
      ownDepthAt: terrain.ownDepth,
      nearWater: terrain.nearChannel,
    });

    const bridges = createBridgeClearance(seed, 'river', {
      ownDepthAt: terrain.ownDepth,
      waterLevelAt: terrain.waterLevel,
      guideAt: terrain.fromChannelCentre,
    });

    /*
      Both, so other biomes keep out of the water *and* out of a crossing. The bridge half is checked second because it is
      by far the more expensive question, and most queries are answered by the water alone.
    */
    function reserved(x, z, radius, context) {
      return water(x, z, radius, context) || bridges(x, z, radius, context);
    }

    /*
      Carried through the composition, because the composed test is still one a biome cannot negotiate its way out
      of — see `createWaterClearance`. The bridge half rides along with it, which is right: a crossing only exists
      where the water does.
    */
    reserved.claimsGround = water.claimsGround;

    return reserved;
  },
};

